import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import AutocompleteEquipmentTypesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteEquipmentTypesTypeahead';
import NumberInput from '~components/FormFields/v2/NumberInput';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { FeatureFlags } from '~constants/featureFlags';
import { Account } from '~hooks/useAccount/models';
import { RemoveItemButton } from '~pages/Sales/sharedSales/RemoveItemButton';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import {
  emptyTruckCapacityRow,
  orderCommittedTruckCapacityRows,
  setTruckCapacitySchemaDefaultValues,
  TruckCapacityDto,
  truckCapacitySchema,
} from './truckCapacitySchema';

export type TruckCapacityPanelHandler = {
  onSubmit: () => Promise<TruckCapacityDto | undefined>;
};

const CAPACITY_ROW_ATTRIBUTE_FIELDS = {
  truck_count: 'count',
  equipment_type_id: 'equipmentType',
} as const;

// Rails reports a nested attribute error as `committed_truck_capacities.<attribute>`, as
// `committed_truck_capacities[<index>].<attribute>` when the association indexes its errors,
// and as a bare `committed_truck_capacities` when it fails the association as a whole.
const CAPACITY_ROW_ERROR_FIELD = /^committed_truck_capacities(?:\[(\d+)\])?(?:\.(\w+))?$/;

function toFormField(apiField: string, submittedRowIndexes: (number | undefined)[]) {
  const field = apiField.replace(/^connected_account\./, '');

  if (field === 'truck_count') {
    return 'truckCount' as const;
  }

  const match = field.match(CAPACITY_ROW_ERROR_FIELD);

  if (!match) {
    return undefined;
  }

  const [, index, attribute] = match;
  const rowAttribute =
    CAPACITY_ROW_ATTRIBUTE_FIELDS[
      attribute as keyof typeof CAPACITY_ROW_ATTRIBUTE_FIELDS
    ];
  // The API indexes an error against the payload it received, so the message only
  // anchors to a row when that payload position still maps to a visible row.
  const rowIndex = index === undefined ? undefined : submittedRowIndexes[Number(index)];

  if (rowIndex === undefined || rowAttribute === undefined) {
    return 'truckCount' as const;
  }

  return `committedTruckCapacities.${rowIndex}.${rowAttribute}` as const;
}

interface TruckCapacityPanelProps {
  account?: Account;
  allowTypedRows?: boolean;
  apiResponseErrors?: ModelError_Item[];
  companyId: string;
  isConnectedAccount?: boolean;
}

const TruckCapacityPanel = forwardRef<TruckCapacityPanelHandler, TruckCapacityPanelProps>(
  function TruckCapacityPanel(
    { account, allowTypedRows, apiResponseErrors, companyId, isConnectedAccount },
    ref,
  ) {
    const isTypedTruckCapacityEnabled = useFeatureFlag({
      featureFlagKey: FeatureFlags.typedTruckCapacityV1,
    });

    const {
      clearErrors,
      control,
      formState,
      getValues,
      handleSubmit,
      setError,
      setValue,
    } = useForm<TruckCapacityDto>({
      resolver: yupResolver(truckCapacitySchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: setTruckCapacitySchemaDefaultValues(isConnectedAccount, account),
    });

    // Payload position -> visible row index for the last submit, so an indexed API
    // error lands on the row that actually produced that payload entry.
    const submittedRowIndexes = useRef<(number | undefined)[]>([]);

    const { append, fields, remove } = useFieldArray({
      control,
      name: 'committedTruckCapacities',
      keyName: 'fieldId',
    });

    const allRows = useWatch({ control, name: 'committedTruckCapacities' });
    // Everywhere else the panel is a scalar truck count, as it was before typed capacity.
    const showTypedRows = Boolean(isTypedTruckCapacityEnabled && allowTypedRows);
    const rows = showTypedRows ? allRows : [];
    const hasRows = rows.length > 0;
    const committedTruckTotal = rows.reduce(
      (total, row) => total + (Number(row.count) || 0),
      0,
    );
    const selectedEquipmentTypeIds = rows.map((row) => row.equipmentType?.value);
    const rowsError = formState.errors.committedTruckCapacities?.message;

    useEffect(() => {
      if (hasRows) {
        setValue('truckCount', committedTruckTotal);
      }
    }, [committedTruckTotal, hasRows, setValue]);

    useEffect(() => {
      clearErrors();

      apiResponseErrors?.forEach((error) => {
        const formField = toFormField(error.field, submittedRowIndexes.current);

        if (formField) {
          setError(formField, { type: 'manual', message: error.message });
        }
      });
    }, [apiResponseErrors, clearErrors, setError]);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            handleSubmit(
              (data) => {
                submittedRowIndexes.current = showTypedRows
                  ? orderCommittedTruckCapacityRows(data)
                  : [];

                resolve(
                  showTypedRows
                    ? data
                    : {
                        ...data,
                        committedTruckCapacities: [],
                        removedCommittedTruckCapacityIds: [],
                      },
                );
              },
              () => {
                resolve(undefined);
              },
            )();
          });
        },
      }),
      [handleSubmit, showTypedRows],
    );

    const removeRow = (index: number) => {
      const removedId = getValues(`committedTruckCapacities.${index}.id`);

      if (removedId) {
        setValue('removedCommittedTruckCapacityIds', [
          ...getValues('removedCommittedTruckCapacityIds'),
          removedId,
        ]);
      }

      const isRemovingLastRow = fields.length === 1;

      remove(index);

      if (isRemovingLastRow) {
        setValue('truckCount', 0);
      }
    };

    return (
      <Box sx={{ width: '100%' }}>
        <NumberInput
          control={control}
          dataUi="committed-truck-capacity-total"
          isDisabled={hasRows}
          label={t('account.form.committed_truck_capacity')}
          name="truckCount"
        />

        {showTypedRows &&
          fields.map((field, index) => (
            <Box
              key={field.fieldId}
              data-ui={`committed-truck-capacity-row-${index}`}
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2 }}
            >
              <AutocompleteEquipmentTypesTypeahead
                companyId={companyId}
                control={control}
                excludedOptionValues={selectedEquipmentTypeIds.filter(
                  (_, selectedIndex) => selectedIndex !== index,
                )}
                isRequired
                label={t('form_fields.equipment_type')}
                name={`committedTruckCapacities.${index}.equipmentType`}
                sx={{ flexGrow: 1 }}
              />

              <NumberInput
                control={control}
                dataUi={`committed-truck-capacity-count-${index}`}
                isRequired
                label={t('form_fields.trucks')}
                name={`committedTruckCapacities.${index}.count`}
                sx={{ width: '120px' }}
              />

              {showTypedRows && (
                <RemoveItemButton
                  action={() => removeRow(index)}
                  // 27px lines the 30px button up with the input below the field label
                  sx={{ cursor: 'pointer', mt: '27px' }}
                />
              )}
            </Box>
          ))}

        {rowsError && <FormHelperText error>{rowsError}</FormHelperText>}

        {showTypedRows && (
          <PseudoLink
            action={() => append(emptyTruckCapacityRow())}
            dataUi="add-truck-type"
            sx={{ display: 'inline-block', mt: 2 }}
            title={t('account.form.add_truck_type')}
          />
        )}
      </Box>
    );
  },
);

export default TruckCapacityPanel;
